/**
 * P2-10 · Storefront listing input shape.
 *
 * The single query contract behind every product grid. Filters are the base
 * facet set (price, availability, on-sale, manufacturer, option values);
 * P2-11 renders controls over this shape rather than adding its own inputs.
 * Sort keys `relevance`/`best-selling`/`rating` are forward hooks (P3/P4/P5)
 * that fall back to a deterministic newest-first ordering until those land.
 */
import { z } from "zod";

import {
  PLP_DEFAULT_PAGE_SIZE,
  PLP_MAX_PAGE,
  PLP_MAX_PAGE_SIZE,
} from "~/config/storefront";
import { minorAmountSchema } from "~/server/api/schemas/product";

export const listingSortSchema = z.enum([
  "relevance",
  "price-asc",
  "price-desc",
  "newest",
  "best-selling",
  "rating",
  "manual",
]);
export type ListingSort = z.infer<typeof listingSortSchema>;

export const listingFiltersSchema = z
  .object({
    /** Products whose [min..max] price range intersects the requested band. */
    priceMinMinor: minorAmountSchema.optional(),
    priceMaxMinor: minorAmountSchema.optional(),
    inStock: z.boolean().optional(),
    onSale: z.boolean().optional(),
    /** The storefront "brand" facet — Manufacturer (P1-05), not the tenant. */
    manufacturerIds: z.array(z.string()).min(1).max(50).optional(),
    /** Product tags linked from PDP metadata chips. */
    tagSlugs: z.array(z.string().trim().min(1).max(100)).min(1).max(50).optional(),
    /**
     * Option-value facets (colour, size, …): AND across entries, OR within an
     * entry's values. Names match case-insensitively — they're brand data.
     */
    options: z
      .array(
        z.object({
          name: z.string().trim().min(1).max(50),
          values: z.array(z.string().trim().min(1).max(100)).min(1).max(50),
        }),
      )
      .max(10)
      .optional(),
    /**
     * P15-20 · Structured-attribute facets (`AttributeDefinition.isFilterable`).
     * Keyed by the definition's brand-unique slug; AND across entries, OR
     * within an entry's values — the same discipline as `options`. Values are
     * the stored ENUM option / TEXT value verbatim, or `"true"`/`"false"` for a
     * BOOLEAN definition. A slug no filterable definition owns simply matches
     * nothing (the predicate re-checks `isFilterable`, so unmarking the flag
     * kills the filter, not just the facet).
     */
    attributes: z
      .array(
        z.object({
          slug: z.string().trim().min(1).max(64),
          values: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
          minNumber: z.number().finite().optional(),
          maxNumber: z.number().finite().optional(),
        }).refine(
          (attribute) => attribute.values.length > 0 || attribute.minNumber !== undefined || attribute.maxNumber !== undefined,
          { message: "An attribute filter needs values or a numeric bound." },
        ).refine(
          (attribute) => attribute.minNumber === undefined || attribute.maxNumber === undefined || attribute.minNumber <= attribute.maxNumber,
          { message: "minNumber must be ≤ maxNumber", path: ["minNumber"] },
        ),
      )
      .max(10)
      .optional(),
  })
  .refine(
    (f) =>
      f.priceMinMinor === undefined ||
      f.priceMaxMinor === undefined ||
      f.priceMinMinor <= f.priceMaxMinor,
    { message: "priceMinMinor must be ≤ priceMaxMinor", path: ["priceMinMinor"] },
  );
export type ListingFilters = z.infer<typeof listingFiltersSchema>;

export const listingInputSchema = z.object({
  /** Scopes to this category and its descendants (materialised path, P1-04). */
  categoryId: z.string().optional(),
  filters: listingFiltersSchema.optional(),
  sort: listingSortSchema.default("relevance"),
  /** Offset mode (numbered pages). Ignored when `cursor` is present. */
  page: z.number().int().min(1).max(PLP_MAX_PAGE).default(1),
  pageSize: z
    .number()
    .int()
    .min(1)
    .max(PLP_MAX_PAGE_SIZE)
    .default(PLP_DEFAULT_PAGE_SIZE),
  /** Cursor mode (load-more, P2-13): resume after this product id (cuid). */
  cursor: z.string().max(64).optional(),
  /** Compute facet aggregations (P2-11 rail) — costs extra bounded queries. */
  includeFacets: z.boolean().default(false),
  /**
   * P15-19 · Apply this category's merchandising rules to the read.
   *
   * **Default false, and that is the interesting part.** Every other caller of
   * this shape — homepage product blocks, upsells, the shop-all grid (P15-18),
   * recommendations (P15-10), `/search` — is asking for products, not for a
   * curated shelf, and none of them should acquire pins because someone added a
   * field. Only the category PLP opts in, and only when the shopper has left
   * the sort alone: an explicit sort is a question, and answering it with the
   * shop's preferred order instead is what P13-08 refused to do on `/search`.
   *
   * Inert without `categoryId`, and inert past page one. See
   * `resolveMerchandisedPins` in `~/server/catalog/listing` for what it does and
   * what stands it down.
   */
  merchandise: z.boolean().default(false),
});
export type ListingInput = z.infer<typeof listingInputSchema>;
